import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import KPICard from "@/components/KPICard";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Users, 
  Target, 
  FileCheck, 
  Clock,
  Plus,
  Loader2,
  Send,
  Eye,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [concession, setConcession] = useState(null);
  const [seasonality, setSeasonality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quarterlyHistory, setQuarterlyHistory] = useState([]);
  const navigate = useNavigate();

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.ceil(currentMonth / 3);

  // Check if this is a Fleet concession (quarterly)
  const isFleet = concession?.is_fleet === true || concession?.objective_type === "quarterly";

  const quarterNames = ["T1", "T2", "T3", "T4"];
  const quarterMonths = {
    1: [1, 2, 3],
    2: [4, 5, 6],
    3: [7, 8, 9],
    4: [10, 11, 12]
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, sellersRes, concessionRes] = await Promise.all([
        api.get("/statistics/dashboard"),
        api.get("/sellers"),
        user?.concession_id ? api.get(`/concessions/${user.concession_id}`) : Promise.resolve({ data: null })
      ]);
      setStats(statsRes.data);
      setSellers(sellersRes.data);
      setConcession(concessionRes.data);
      
      const isFleetConcession = concessionRes.data?.is_fleet === true;
      
      // For Fleet: fetch quarterly objectives, otherwise monthly
      if (isFleetConcession) {
        // Fetch objectives for current quarter (first month of quarter)
        const firstMonthOfQuarter = quarterMonths[currentQuarter][0];
        const objectivesRes = await api.get("/objectives", { 
          params: { month: firstMonthOfQuarter, year: currentYear } 
        });
        setObjectives(objectivesRes.data);
        
        // Fetch historical quarterly data (last 4 quarters)
        const historyPromises = [];
        for (let i = 0; i < 4; i++) {
          let q = currentQuarter - i;
          let y = currentYear;
          if (q <= 0) {
            q += 4;
            y -= 1;
          }
          const month = quarterMonths[q][0];
          historyPromises.push(
            api.get("/objectives", { params: { month, year: y } })
              .then(res => ({ quarter: q, year: y, objectives: res.data }))
              .catch(() => ({ quarter: q, year: y, objectives: [] }))
          );
        }
        const history = await Promise.all(historyPromises);
        // Sort chronologically: oldest → newest (left → right)
        history.sort((a, b) => a.year - b.year || a.quarter - b.quarter);
        setQuarterlyHistory(history);
      } else {
        // Summer merge: for non-Fleet, from 2026 onward, August has no separate objectives (they live under July)
        const fetchMonth = (currentMonth === 8 && currentYear >= 2026) ? 7 : currentMonth;
        const objectivesRes = await api.get("/objectives", { 
          params: { month: fetchMonth, year: currentYear } 
        });
        setObjectives(objectivesRes.data);
        
        // Fetch seasonality data for non-Fleet (use the effective month)
        if (concessionRes.data?.name) {
          try {
            const seasonRes = await api.get(`/seasonality/${encodeURIComponent(concessionRes.data.name)}/${fetchMonth}`);
            setSeasonality(seasonRes.data);
          } catch (e) {
            console.log("No seasonality data");
          }
        }
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: "Brouillon", className: "bg-gray-100 text-gray-700" },
      pending_seller: { label: "Attente vendeur", className: "bg-amber-100 text-amber-700" },
      pending_manager: { label: "À signer", className: "bg-blue-100 text-blue-700" },
      signed: { label: "Signé", className: "bg-green-100 text-green-700" }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const monthNames = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  // Calculate totals
  const totalVN = objectives.reduce((sum, obj) => sum + (obj.vn_target || 0), 0);
  const totalVO = objectives.reduce((sum, obj) => sum + (obj.vo_target || 0), 0);
  const totalFinancing = objectives.reduce((sum, obj) => sum + (obj.financing_target || 0), 0);

  // Get sellers eligible for current month (based on creation date)
  const eligibleSellers = sellers.filter(seller => {
    if (!seller.created_at) return true;
    const createdDate = new Date(seller.created_at);
    const createdYear = createdDate.getFullYear();
    const createdMonth = createdDate.getMonth() + 1;
    if (createdYear < currentYear) return true;
    if (createdYear === currentYear && createdMonth <= currentMonth) return true;
    return false;
  });

  // Sellers without objectives this month
  const sellersWithoutObjectives = eligibleSellers.filter(
    s => !objectives.find(o => o.seller_id === s.id)
  );

  if (loading) {
    return (
      <Layout title="Tableau de bord">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={concession ? `Tableau de bord - ${concession.name}${isFleet ? ' (Fleet)' : ''}` : "Tableau de bord"}>
      {/* Fleet Badge */}
      {isFleet && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 text-white px-3 py-1 rounded font-bold">FLEET</div>
            <div>
              <p className="font-semibold text-blue-900">Mode Trimestriel</p>
              <p className="text-sm text-blue-700">
                Trimestre actuel : {quarterNames[currentQuarter - 1]} {currentYear} ({sellers.length} vendeurs)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards - 3 cartes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="manager-dashboard-kpis">
        <KPICard
          value={sellersWithoutObjectives.length}
          label={isFleet ? "Sans objectif ce trimestre" : "Sans objectif ce mois"}
          icon={Users}
          color="blue"
        />
        <KPICard
          value={objectives.filter(o => o.status === "pending_seller" || o.status === "pending_manager").length}
          label="En attente de signature"
          icon={Clock}
          color="amber"
        />
        <KPICard
          value={objectives.filter(o => o.status === "signed").length}
          label="Signés"
          icon={FileCheck}
          color="green"
        />
      </div>

      {/* Current Period Objectives with Totals */}
      <div className="card-dashboard" data-testid="current-objectives-table">
        <div className="flex items-center justify-between mb-4">
          <h2 
            className="text-lg font-bold text-[#212E38]"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            {isFleet 
              ? `Objectifs ${quarterNames[currentQuarter - 1]} ${currentYear}`
              : `Objectifs ${(currentMonth === 7 || currentMonth === 8) && currentYear >= 2026 ? 'Juillet-Août' : monthNames[currentMonth]} ${currentYear}`
            }
          </h2>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/objectives")}
          >
            Voir tout
          </Button>
        </div>

        {/* Totals and Seasonality Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Totals */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-[#E30613]" />
              Total objectifs fixés
            </h3>
            <div className={isFleet ? "" : "grid grid-cols-3 gap-4"}>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1a2744]">{totalVN}</p>
                <p className="text-xs text-gray-500">VN</p>
              </div>
              {!isFleet && (
                <>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#1a2744]">{totalVO}</p>
                    <p className="text-xs text-gray-500">VO</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#1a2744]">{totalFinancing.toLocaleString()}€</p>
                    <p className="text-xs text-gray-500">Financement</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Seasonality - only for non-Fleet */}
          {!isFleet && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="text-sm font-semibold text-[#1a2744] mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#1a2744]" />
                Saisonnalité - Objectifs conseillés
              </h3>
              {seasonality ? (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#1a2744]">{seasonality.budget_vn || 0}</p>
                    <p className="text-xs text-gray-600">VN ({seasonality.seasonality_vn_percent || 0}%)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#1a2744]">{seasonality.budget_vo || 0}</p>
                    <p className="text-xs text-gray-600">VO ({seasonality.seasonality_vo_percent || 0}%)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-400">-</p>
                    <p className="text-xs text-gray-500">Financement</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-2">
                  Données de saisonnalité non disponibles
                </p>
              )}
            </div>
          )}
        </div>

        {objectives.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendeur</TableHead>
                <TableHead className="text-center">VN</TableHead>
                {!isFleet && (
                  <>
                    <TableHead className="text-center">VO</TableHead>
                    <TableHead className="text-center">Financement</TableHead>
                  </>
                )}
                <TableHead className="text-center">Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {objectives.map((obj) => (
                <TableRow key={obj.id}>
                  <TableCell className="font-medium">{obj.seller_name}</TableCell>
                  <TableCell className="text-center">{obj.vn_target}</TableCell>
                  {!isFleet && (
                    <>
                      <TableCell className="text-center">{obj.vo_target}</TableCell>
                      <TableCell className="text-center">{obj.financing_target?.toLocaleString()}€</TableCell>
                    </>
                  )}
                  <TableCell className="text-center">{getStatusBadge(obj.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {obj.status === "pending_manager" && (
                        <Button
                          size="sm"
                          onClick={() => navigate(`/sign/${obj.id}`)}
                          className="btn-primary"
                          data-testid={`sign-objective-${obj.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Signer
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/objectives/${obj.id}/edit`)}
                        data-testid={`view-objective-${obj.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="empty-state">
            <Target className="empty-state-icon" />
            <p>Aucun objectif pour ce mois</p>
            <Button
              onClick={() => navigate("/objectives")}
              className="mt-4 btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un objectif
            </Button>
          </div>
        )}
      </div>

      {/* Quarterly History for Fleet */}
      {isFleet && quarterlyHistory.length > 0 && (
        <div className="card-dashboard mt-6" data-testid="quarterly-history">
          <h2 
            className="text-lg font-bold text-[#212E38] mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            <TrendingUp className="w-5 h-5 inline mr-2 text-blue-600" />
            Historique Trimestriel Fleet
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quarterlyHistory.map(({ quarter, year, objectives: qObjs }) => {
              const totalVN = qObjs.reduce((sum, o) => sum + (o.vn_target || 0), 0);
              const totalVO = qObjs.reduce((sum, o) => sum + (o.vo_target || 0), 0);
              const totalDFS = qObjs.reduce((sum, o) => sum + (o.financing_target || 0), 0);
              const signed = qObjs.filter(o => o.status === "signed").length;
              const isCurrentQuarter = quarter === currentQuarter && year === currentYear;
              
              return (
                <div 
                  key={`${quarter}-${year}`}
                  className={`p-4 rounded-xl border-2 ${
                    isCurrentQuarter 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-1 rounded font-bold text-sm ${
                      isCurrentQuarter ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {quarterNames[quarter - 1]} {year}
                    </span>
                    {isCurrentQuarter && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">Actuel</Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">VN</span>
                      <span className="font-semibold">{totalVN}</span>
                    </div>
                    <div className="pt-2 border-t mt-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Signés</span>
                        <span className="font-semibold text-green-600">{signed}/{qObjs.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sellers without objectives this month/quarter */}
      {sellersWithoutObjectives.length > 0 && (
        <div className="card-dashboard mt-6" data-testid="sellers-without-objectives">
          <h2 
            className="text-lg font-bold text-[#212E38] mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Vendeurs sans objectif {isFleet ? 'ce trimestre' : 'ce mois'}
          </h2>
          <div className="space-y-2">
            {sellersWithoutObjectives.map((seller) => (
              <div 
                key={seller.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-[#212E38]">{seller.name}</p>
                  <p className="text-sm text-gray-500">{seller.email}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate(`/objectives`)}
                  className="btn-primary"
                  data-testid={`create-for-${seller.id}`}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Créer objectif
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
